import { type Ref } from 'react'

/**
 * Merges multiple refs into a single ref callback
 *
 * @warning Ensure all refs are compatible types to avoid runtime errors.
 * All refs will receive the same element.
 *
 * @example Safe usage (recommended):
 * ```tsx
 * const ref1 = useRef<HTMLDivElement>(null)
 * const ref2 = useRef<HTMLDivElement>(null)
 * const mergedRef = mergeRefs(ref1, ref2) // Type: (ref: HTMLDivElement | null) => void
 * ```
 *
 * @example Type coercion (use with caution):
 * ```tsx
 * const divRef = useRef<HTMLDivElement>(null)
 * const spanRef = useRef<HTMLSpanElement>(null)
 * const mergedRef = mergeRefs(divRef, spanRef) // Type: (ref: HTMLElement | null) => void
 * // ⚠️ Risk: spanRef might contain a div element, not a span
 * ```
 *
 * @param refs - Array of refs to merge (can include undefined)
 * @returns A single ref callback that updates all provided refs
 */
export function mergeRefs<T>(...refs: (Ref<T> | undefined)[]): (ref: T | null) => void {
  return (ref: T | null) => {
    for (const r of refs) {
      if (!r) {
        continue
      }

      if (typeof r === 'function') {
        r(ref)
      } else {
        r.current = ref
      }
    }
  }
}
